/**
 * DOM으로 전달할 데이터 속성을 필터링하고 포맷팅하는 유틸리티 함수
 */

/**
 * DOM에 전달되어야 하는 데이터 테스트 ID와 데이터 속성을 추출
 *
 * @param props 컴포넌트 props
 * @returns 데이터 테스트 ID와 데이터 속성을 포함한 객체
 */
export const extractDataAttributes = (props: Record<string, unknown>) => {
  const dataAttributes: Record<string, unknown> = {};

  // 모든 props를 순회하며 'data-' 시작하는 속성과 'data-testid'를 추출
  Object.keys(props).forEach((key) => {
    if (key === 'data-testid' || key.startsWith('data-')) {
      dataAttributes[key] = props[key];
    }
  });

  return dataAttributes;
};

/**
 * Boolean 속성을 문자열로 변환
 * React에서 boolean 속성이 DOM으로 전달될 때 문자열로 변환되도록 하는 함수
 *
 * @param props 컴포넌트 props
 * @param booleanProps 문자열로 변환할 boolean 속성 리스트
 * @returns 문자열로 변환된 속성을 포함한 객체
 */
export const prepareBooleanAttributes = (
  props: Record<string, unknown>,
  booleanProps: string[],
) => {
  const processedProps: Record<string, unknown> = { ...props };

  booleanProps.forEach((propName) => {
    if (propName in processedProps && typeof processedProps[propName] === 'boolean') {
      processedProps[propName] = processedProps[propName] ? 'true' : 'false';
    }
  });

  return processedProps;
};

/**
 * DOM으로 전달하지 않을 props 필터링
 *
 * @param props 원본 props
 * @param excludeProps 제외할 props 리스트
 * @returns 필터링된 props 객체
 */
export const filterProps = (props: Record<string, unknown>, excludeProps: string[]) => {
  const filteredProps: Record<string, unknown> = { ...props };

  excludeProps.forEach((prop) => {
    delete filteredProps[prop];
  });

  return filteredProps;
};

/**
 * styled-components에서 DOM으로 전달하지 않을 props 리스트
 * React가 인식하지 못하는 속성들을 제외하는 데 사용
 */
export const styledSystemProps = [
  // UI 상태 관련 속성
  'isActive',
  'isDisabled',
  'isLoading',
  'fullWidth',
  'fullPage',
  'isHidden',
  'hasError',
  'isExiting',
  'position',
  'isVisible',

  // 커스텀 스타일 속성
  'variant',
  'size',
  'leftIcon',
  'rightIcon',
  'status',
  'helperText',

  // 테이블 관련 속성
  'sortable',
  'collapsed',
  'hover',

  // 레이아웃 속성
  'gap',
  'columns',
  'colSpan',
  'rowSpan',

  // 기타 커스텀 속성
  'collapsible',
  'hasLeftIcon',
  'hasRightIcon',
];
